import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, Redirect, useParams } from 'react-router-dom';
import Layout from 'antd/lib/layout/layout';
import Text from 'antd/lib/typography/Text';
import { AppComponentProps } from '@/typescriptHelpers';
import { useTrackPageView } from '@/helpers/trackingHelpers';
import { trackAppRouteEvent } from '@/actions/trackEvent';
import { Event } from '@/utils/analytics';
import { RootState } from '@/reducers/reducers';
import { selectAppSettings } from '@/selectors/selectors';
import { createAppRoute } from '@/helpers/routeHelpers';
import { antGrey, colorWithAlpha, rwColors } from '@/components/styles/globalStyles';
import routes from '@/routes';
import { TriageViewType } from '@/actions/triage';
import { TriageIssuesList } from './TriageIssuesList';
import { RoutedTriageIssueDrawer } from './TriageIssueDrawer';

type TriagePageProps = AppComponentProps & { releaseId?: string };

const TriagePage: React.FC<TriagePageProps> = (props: TriagePageProps) => {
    const enableTriage = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId)?.enableTriage ?? false,
    );
    const { issueIdParam } = useParams<{ issueIdParam?: string }>();
    const basePath =
        props.releaseId ?
            generatePath(routes.releaseTriage, {
                orgIdParam: props.orgId,
                appIdParam: props.appId,
                releaseIdParam: props.releaseId,
            })
        :   generatePath(routes.appTriage, { orgIdParam: props.orgId, appIdParam: props.appId });
    const viewType = props.releaseId ? TriageViewType.Release : TriageViewType.App;

    useTrackPageView('Triage');

    const dispatch = useDispatch();
    React.useEffect(() => {
        dispatch(
            trackAppRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                eventName: Event.ViewTriage,
                viewType: props.releaseId ? 'release' : 'app',
            }),
        );
    }, [dispatch, props.orgId, props.appId, props.releaseId]);

    if (!enableTriage) {
        return <Redirect to={createAppRoute(props.orgId, props.appId)} />;
    }

    return (
        <Layout
            style={{
                overflowY: 'auto',
                padding: 12,
            }}
        >
            <div
                style={{
                    backgroundColor: colorWithAlpha(rwColors.primary, 0.1),
                    padding: '24px 32px',
                }}
            >
                <Text
                    style={{
                        color: antGrey.grey7,
                        fontSize: 16,
                        fontWeight: 600,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                    }}
                >
                    Triage
                </Text>
            </div>
            <TriageIssuesList orgId={props.orgId} appId={props.appId} releaseId={props.releaseId} basePath={basePath} />
            <RoutedTriageIssueDrawer
                orgId={props.orgId}
                appId={props.appId}
                basePath={basePath}
                issueId={issueIdParam}
                viewType={viewType}
            />
        </Layout>
    );
};

export default TriagePage;
